import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { opportunitiesApi } from '../../api/opportunities'
import { RefusedError } from '../../api/client'
import { DocumentDrop } from '../../components/Documents/DocumentDrop'
import { DocumentFacts } from '../../components/Documents/DocumentFacts'
import { DocumentMiniList } from '../../components/Documents/DocumentList'
import { formatDate } from '../../lib/format'
import type { NewOpportunity, OpportunityFocus, OpportunityOption, PersonNode, Workbench } from '../../types/workbench'
import { STAGE_WORDS } from '../Deals/stages'
import { countsLine, foldSummary } from './opportunityFocus'

// THE OPPORTUNITY (REQ-121) — chosen or made BEFORE a draft is crafted. Owner, 2026-09-28: the workbench felt "like
// we're crafting an email without properly selecting or creating an opportunity first, which may have multiple
// correspondences and sales tactics applied against it". This panel sits above the conversation: the opportunity the
// message serves, or the account's opportunities to pick from and a form to set one up. A derived opening ("Openings
// here") is a proposal until the rep makes it one here; "Make this an opportunity" arrives as a prefill.

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))
const stageWords = (stage: string) => (STAGE_WORDS as Record<string, string>)[stage] ?? stage

/** The stages an opportunity may be set up in: the deal stages, `none` aside. */
export const STAGES = ['lead', 'opportunity', 'proposal', 'negotiation', 'won', 'lost', 'delivery', 'dormant'] as const

/** What "Make this an opportunity" hands the form; the nonce makes the same opening pressed twice open it twice. */
export interface NewOpportunityPrefill { subject: string; summary?: string; serviceLine?: string; openingId?: string; personIds: string[]; nonce: number }

interface FormState { subject: string; stage: string; summary: string; serviceLine: string; personIds: string[]; openingId?: string }
const EMPTY: FormState = { subject: '', stage: 'opportunity', summary: '', serviceLine: '', personIds: [] }

export function OpportunityPanel({ spoke, companyId, wb, focusedId, onPick, prefill, busy = false }: {
  spoke: string; companyId: string; wb: Workbench
  /** the ?opportunity= on the route; the focus is absent when it is not one of the rep's */
  focusedId: string | null
  onPick: (engagementId: string) => void
  prefill: NewOpportunityPrefill | null
  busy?: boolean
}) {
  const qc = useQueryClient()
  const focus = wb.focus
  const pursuits = wb.pursuits ?? []
  const people = useMemo(() => wb.nodes.filter((n): n is PersonNode => n.kind === 'person' && !n.background), [wb.nodes])
  const [picking, setPicking] = useState(!focus)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY)
  // the picker closes when an opportunity arrives on the route, and opens when none is there (keyed on the id, not the object)
  const focusedEngagementId = focus?.engagementId ?? null
  useEffect(() => { setPicking(!focusedEngagementId); if (focusedEngagementId) setCreating(false) }, [focusedEngagementId])
  // an opening made an opportunity: the form opens filled from it (keyed on the nonce, so the same opening pressed twice opens it twice)
  const prefillNonce = prefill?.nonce ?? 0
  useEffect(() => {
    if (!prefill || !prefillNonce) return
    setForm({ subject: prefill.subject, stage: 'opportunity', summary: prefill.summary ?? '', serviceLine: prefill.serviceLine ?? '', personIds: prefill.personIds, openingId: prefill.openingId })
    setPicking(true); setCreating(true)
  }, [prefillNonce]) // prefill itself is read inside; the nonce is the change signal

  const create = useMutation({
    mutationFn: (body: NewOpportunity) => opportunitiesApi.create(spoke, body),
    onSuccess: (made) => {
      setCreating(false); setPicking(false); setForm(EMPTY)
      void qc.invalidateQueries({ queryKey: ['workbench', spoke, companyId] })
      onPick(made.engagementId)
    },
  })
  const submit = () => {
    const subject = form.subject.trim()
    if (!subject) return
    create.mutate({
      companyId, subject, stage: form.stage,
      summary: form.summary.trim() || undefined, serviceLine: form.serviceLine.trim() || undefined,
      personIds: form.personIds.length > 0 ? form.personIds : undefined, openingId: form.openingId,
    })
  }
  const togglePerson = (id: string) => setForm((f) => ({ ...f, personIds: f.personIds.includes(id) ? f.personIds.filter((x) => x !== id) : [...f.personIds, id] }))

  return (
    <div className="opportunity-panel" role="region" aria-label="Opportunity">
      <div className="opportunity-panel-head">
        <p className="kicker">Opportunity</p>
        {focus && (picking
          ? <button type="button" className="button small" onClick={() => { setPicking(false); setCreating(false) }}>Keep this one</button>
          : <button type="button" className="button small" onClick={() => setPicking(true)} disabled={busy}>Change</button>)}
      </div>

      {focus ? <FocusCard focus={focus} spoke={spoke} companyId={companyId} /> : (
        <p className="small opportunity-ask">
          {focusedId ? 'The opportunity in the address is not one of yours here. ' : ''}Pick the opportunity this message serves before drafting.
        </p>
      )}

      {picking && (
        <div className="opportunity-choices">
          {pursuits.length === 0
            ? <p className="muted small">No opportunity on file at this account yet; set one up below.</p>
            : (
              <ul className="plain opportunity-options">
                {pursuits.map((o) => <OptionRow key={o.engagementId} o={o} current={o.engagementId === focus?.engagementId} busy={busy} onPick={() => onPick(o.engagementId)} />)}
              </ul>
            )}
          {!creating
            ? <button type="button" className="button small" onClick={() => { setForm(EMPTY); setCreating(true); create.reset() }} disabled={busy}>New opportunity</button>
            : (
              <form className="opportunity-form" onSubmit={(e) => { e.preventDefault(); submit() }} aria-label="New opportunity">
                <label>Subject
                  <input type="text" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} required autoFocus placeholder="What the pursuit is, in a line" disabled={create.isPending} />
                </label>
                <div className="opportunity-form-row">
                  <label>Stage
                    <select value={form.stage} onChange={(e) => setForm((f) => ({ ...f, stage: e.target.value }))} disabled={create.isPending}>
                      {STAGES.map((s) => <option key={s} value={s}>{stageWords(s)}</option>)}
                    </select>
                  </label>
                  <label>Service line
                    <input type="text" value={form.serviceLine} onChange={(e) => setForm((f) => ({ ...f, serviceLine: e.target.value }))} placeholder="optional" disabled={create.isPending} />
                  </label>
                </div>
                <label>Summary
                  <textarea rows={3} value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} placeholder="The situation, in your words" disabled={create.isPending} />
                </label>
                <fieldset className="opportunity-people">
                  <legend>People in it</legend>
                  {people.length === 0 ? <span className="muted small">Nobody on the stage yet.</span> : people.map((p) => (
                    <label key={p.personId} className="check">
                      <input type="checkbox" checked={form.personIds.includes(p.personId)} onChange={() => togglePerson(p.personId)} disabled={create.isPending} />
                      <span>{p.label}{p.title ? <span className="muted"> · {p.title}</span> : null}</span>
                    </label>
                  ))}
                </fieldset>
                {form.openingId && <p className="muted small">From the opening read at this account; the reading stays on file as where it came from.</p>}
                {create.isError && <p className="error small" role="alert">{why(create.error)}</p>}
                <div className="actions">
                  <button type="submit" className="button small primary" disabled={create.isPending || !form.subject.trim()}>{create.isPending ? 'Setting up…' : 'Set it up and work it'}</button>
                  <button type="button" className="button small" onClick={() => { setCreating(false); create.reset() }} disabled={create.isPending}>Cancel</button>
                </div>
              </form>
            )}
        </div>
      )}
    </div>
  )
}

/** The chosen opportunity: subject (to its page), stage, where it came from, the summary, the next step, the people, the counts,
 *  and its documents (REQ-119): a drop zone for one more, the rows the focus carries, and what they said. */
function FocusCard({ focus, spoke, companyId }: { focus: OpportunityFocus; spoke: string; companyId: string }) {
  const qc = useQueryClient()
  const [more, setMore] = useState(false)
  const summary = foldSummary(focus.summary)
  // the focus carries its documents; a new upload or a reading that lands re-reads the workbench so they follow
  const refreshWorkbench = useCallback(() => { void qc.invalidateQueries({ queryKey: ['workbench', spoke, companyId] }) }, [qc, spoke, companyId])
  const documents = focus.documents ?? []
  const documentFacts = focus.documentFacts ?? []
  return (
    <div className="opportunity-focus">
      <p className="opportunity-title">
        <Link to={`/opportunities/${encodeURIComponent(focus.engagementId)}`}><strong>{focus.subject}</strong></Link>
        <span className={`tag stage-tag ${focus.stage}`}>{stageWords(focus.stage)}</span>
        <span className="muted small">{focus.fromCrm ? "on the firm's CRM" : focus.byHand ? 'set up by you' : 'read from the mail'}{focus.serviceLine ? ` · ${focus.serviceLine}` : ''}</span>
      </p>
      {focus.summary && (
        <p className="small opportunity-summary">
          {more ? focus.summary : summary.text}
          {summary.folded && <> <button type="button" className="link-button small" onClick={() => setMore((m) => !m)}>{more ? 'less' : 'more'}</button></>}
        </p>
      )}
      {focus.nextStep && <p className="small opportunity-next"><span className="muted">Next:</span> {focus.nextStep}{focus.nextStepBy ? <span className="muted"> · by {formatDate(focus.nextStepBy)}</span> : null}</p>}
      {focus.people.length > 0 && (
        <ul className="chips opportunity-people-chips" aria-label="People in it">
          {focus.people.map((p) => <li key={p.personId} className="chip on" title={[p.title, p.role].filter(Boolean).join(' · ') || undefined}>{p.name}</li>)}
        </ul>
      )}
      <p className="muted small opportunity-counts">{countsLine(focus)}</p>
      <div className="opportunity-documents">
        <DocumentDrop spoke={spoke} origin="workbench" targets={{ engagementId: focus.engagementId }} compact onUploaded={refreshWorkbench} />
        <DocumentMiniList spoke={spoke} documents={documents} onLanded={refreshWorkbench} />
        {documentFacts.length > 0 && <DocumentFacts facts={documentFacts} compact />}
      </div>
    </div>
  )
}

function OptionRow({ o, current, busy, onPick }: { o: OpportunityOption; current: boolean; busy: boolean; onPick: () => void }) {
  return (
    <li className={`opportunity-option ${current ? 'on' : ''}`}>
      <span className="opportunity-option-words">
        <strong>{o.subject}</strong>
        <span className={`tag stage-tag ${o.stage}`}>{stageWords(o.stage)}</span>
        <span className="muted small">{o.fromCrm ? 'on the CRM' : o.byHand ? 'by hand' : 'from the mail'} · {o.correspondences} {o.correspondences === 1 ? 'message' : 'messages'}{o.lastActivityOn ? ` · ${formatDate(o.lastActivityOn)}` : ''}</span>
      </span>
      <button type="button" className={`button small ${current ? '' : 'primary'}`} onClick={onPick} disabled={busy || current} aria-pressed={current}>{current ? 'Working it' : 'Work this'}</button>
    </li>
  )
}
